import { useEffect, useState } from "react";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { toast } from "sonner";
import { CalendarHeart, Clock, X, Check, Users, Loader2, Calendar, Stethoscope, ExternalLink } from "lucide-react";

export default function Coaching() {
  const [tab, setTab] = useState("coaches");
  const [coaches, setCoaches] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [slots, setSlots] = useState([]);
  const [picked, setPicked] = useState(null);
  const [notes, setNotes] = useState("");
  const [booking, setBooking] = useState(false);
  const [coachingUrl, setCoachingUrl] = useState("");

  const loadAll = () => {
    api.get("/coaching/coaches").then((r) => setCoaches(r.data)).catch(() => {});
    api.get("/coaching/workshops").then((r) => setWorkshops(r.data)).catch(() => {});
    api.get("/coaching/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/config").then((r) => setCoachingUrl(r.data.healthie_coaching_url)).catch(() => {});
  };
  useEffect(loadAll, []);

  const openCoach = async (c) => {
    setSelectedCoach(c); setPicked(null); setNotes("");
    const { data } = await api.get(`/coaching/slots?coach_id=${c.id}`);
    setSlots(data);
  };

  const confirmBooking = async () => {
    if (!picked) return;
    setBooking(true);
    try {
      await api.post("/coaching/book", { coach_id: selectedCoach.id, date: picked.date, time: picked.time, notes });
      toast.success("Your session is booked! 🌿");
      setSelectedCoach(null);
      loadAll();
      setTab("bookings");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not book session");
    } finally { setBooking(false); }
  };

  const cancel = async (id) => {
    try { await api.delete(`/coaching/bookings/${id}`); loadAll(); toast.success("Session cancelled"); }
    catch { toast.error("Could not cancel"); }
  };

  const registerWorkshop = async (w) => {
    try { await api.post("/coaching/workshops/register", { workshop_id: w.id }); toast.success(`You're registered for "${w.title}"`); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not register"); }
  };

  const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const tabs = [{ k: "coaches", l: "1:1 Coaching" }, { k: "workshops", l: "Workshops" }, { k: "clinical", l: "Practitioner Visit" }, { k: "bookings", l: "My Bookings" }];

  return (
    <AppLayout>
      <div className="animate-fade-up">
        <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#9B7E8D]">Coaching & Workshops</span>
        <h1 className="mt-2 font-serif text-5xl tracking-tight text-wine">Walk with a guide</h1>
        <p className="mt-3 text-base text-[#6E5862] max-w-xl">Book holistic coaching or join a live workshop. Clinical care stays with our partner platform.</p>
      </div>

      <div className="mt-8 flex gap-2.5">
        {tabs.map((t) => (
          <button key={t.k} data-testid={`coaching-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`rounded-full px-5 py-2.5 text-sm font-medium transition-all ${tab === t.k ? "bg-wine text-white" : "bg-white border border-taupe text-[#6E5862] hover:bg-cream"}`}>
            {t.l}{t.k === "bookings" && bookings.filter(b => b.status !== "cancelled").length > 0 ? ` (${bookings.filter(b => b.status !== "cancelled").length})` : ""}
          </button>
        ))}
      </div>

      {tab === "coaches" && (
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {coaches.map((c) => (
            <div key={c.id} data-testid={`coach-card-${c.id}`} className="bg-white rounded-3xl border border-taupe overflow-hidden">
              <img src={c.image_url} alt={c.name} className="w-full h-48 object-cover" />
              <div className="p-6">
                <h3 className="font-serif text-2xl text-[#3A1230]">{c.name}</h3>
                <p className="text-sm text-wine font-medium">{c.title}</p>
                <p className="mt-2 text-xs uppercase tracking-[0.1em] text-[#9B7E8D]">{c.focus}</p>
                <p className="mt-3 text-sm text-[#6E5862]">{c.bio}</p>
                <button data-testid={`coach-book-${c.id}`} onClick={() => openCoach(c)} className="mt-5 inline-flex w-full items-center justify-center rounded-full bg-wine px-6 py-3 text-sm font-medium text-white hover:bg-wine-dark transition-all">
                  Book a session
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "workshops" && (
        <div className="mt-8 grid sm:grid-cols-2 gap-6">
          {workshops.map((w) => (
            <div key={w.id} data-testid={`workshop-card-${w.id}`} className="bg-white rounded-3xl border border-taupe overflow-hidden">
              <img src={w.image_url} alt={w.title} className="w-full h-44 object-cover" />
              <div className="p-6">
                <div className="flex items-center gap-2 text-xs text-[#9B7E8D]"><Calendar className="h-3.5 w-3.5" /> {fmtDate(w.date)} · {w.time}</div>
                <h3 className="mt-2 font-serif text-2xl text-[#3A1230] leading-snug">{w.title}</h3>
                <p className="mt-2 text-sm text-[#6E5862]">{w.description}</p>
                <button
                  data-testid={`workshop-register-${w.id}`}
                  onClick={() => registerWorkshop(w)}
                  disabled={w.registered}
                  className={`mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-all ${w.registered ? "bg-[#EAF0EC] text-[#4A6B53]" : "bg-wine text-white hover:bg-wine-dark"}`}
                >
                  {w.registered ? <><Check className="h-4 w-4" /> Registered</> : <><Users className="h-4 w-4" /> Register</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "clinical" && (
        <div className="mt-8 rounded-[2rem] border border-taupe bg-white overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-8 pt-7 pb-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl bg-wine/10 flex items-center justify-center"><Stethoscope className="h-6 w-6 text-wine" strokeWidth={1.5} /></div>
              <div>
                <h2 className="font-serif text-3xl text-[#3A1230]">Practitioner visit</h2>
                <p className="text-sm text-[#6E5862] mt-0.5">Book a clinical telehealth visit, securely via our partner platform.</p>
              </div>
            </div>
            {coachingUrl && (
              <a href={coachingUrl} target="_blank" rel="noreferrer" data-testid="coaching-open-scheduler"
                className="hidden sm:inline-flex items-center gap-2 rounded-full bg-wine px-6 py-3 text-sm font-medium text-white hover:bg-wine-dark transition-all">
                Open scheduler <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
          {coachingUrl ? (
            <iframe data-testid="coaching-scheduler" src={coachingUrl} title="Book a practitioner visit"
              className="w-full" style={{ height: "720px", border: "0" }} allow="payment" />
          ) : (
            <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-wine" /></div>
          )}
        </div>
      )}

      {tab === "bookings" && (
        <div className="mt-8 space-y-4 max-w-2xl">
          {bookings.filter((b) => b.status !== "cancelled").length === 0 && (
            <div className="bg-white rounded-3xl p-10 border border-taupe text-center">
              <CalendarHeart className="h-10 w-10 text-wine mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-[#6E5862]">No sessions booked yet. Choose a coach to begin.</p>
            </div>
          )}
          {bookings.filter((b) => b.status !== "cancelled").map((b) => (
            <div key={b.id} data-testid={`booking-${b.id}`} className="flex items-center justify-between bg-white rounded-3xl p-6 border border-taupe">
              <div>
                <h3 className="font-serif text-2xl text-[#3A1230]">{b.coach_name}</h3>
                <div className="flex items-center gap-3 mt-1 text-sm text-[#9B7E8D]">
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {fmtDate(b.date)}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {b.time}</span>
                </div>
              </div>
              <button data-testid={`booking-cancel-${b.id}`} onClick={() => cancel(b.id)} className="text-sm text-[#9B4444] hover:underline">Cancel</button>
            </div>
          ))}
        </div>
      )}

      {/* Booking modal */}
      {selectedCoach && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#3A1230]/40 px-5" onClick={() => setSelectedCoach(null)}>
          <div className="bg-white rounded-[2rem] p-8 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-3xl text-wine">Book with {selectedCoach.name}</h2>
              <button data-testid="booking-modal-close" onClick={() => setSelectedCoach(null)}><X className="h-5 w-5 text-[#9B7E8D]" /></button>
            </div>
            <p className="mt-4 text-sm font-medium text-[#6E5862]">Choose a time</p>
            <div className="mt-3 space-y-3">
              {slots.map((s) => (
                <div key={s.date}>
                  <p className="text-xs uppercase tracking-[0.1em] text-[#9B7E8D] mb-2">{fmtDate(s.date)}</p>
                  <div className="flex flex-wrap gap-2">
                    {s.times.map((t) => {
                      const isPicked = picked?.date === s.date && picked?.time === t;
                      return (
                        <button key={t} data-testid={`slot-${s.date}-${t.replace(/[:\s]/g, "")}`} onClick={() => setPicked({ date: s.date, time: t })}
                          className={`rounded-full px-4 py-2 text-sm transition-all ${isPicked ? "bg-wine text-white" : "bg-[#F6ECF0] text-[#6E5862] hover:bg-taupe"}`}>
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <textarea data-testid="booking-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Anything you'd like your coach to know? (optional)"
              className="mt-5 w-full rounded-2xl border border-[#D8C2CD] bg-cream px-4 py-3 text-sm text-[#3A1230] focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine resize-none" />
            <button data-testid="booking-confirm" onClick={confirmBooking} disabled={!picked || booking} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-wine px-6 py-3.5 text-sm font-medium text-white hover:bg-wine-dark disabled:opacity-50 transition-all">
              {booking ? <Loader2 className="h-5 w-5 animate-spin" /> : "Confirm booking"}
            </button>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
